/** Map lifecycle: create from an image, load, rename, delete — all local-first. */
import { newDoc, parseDoc, type MapDoc, type MapKind, type TestStyle } from '../../shared/doc.ts';
import { newId } from '../model/ops.ts';
import { formatDateHuman, todayYYYYMMDD } from '../model/testid.ts';
import { getBlob, mutateMap, type LocalMap } from '../store/idb.ts';
import { now } from './clock.ts';
import { decodeImage, makeThumbnail } from './image.ts';
import { app, currentInitials } from './state.ts';
import { changed, publishMaps, show } from './sync.ts';

export function kindLabel(kind: MapKind): string {
  return kind === 'soils' ? 'Soils' : 'Concrete';
}

export function defaultTitle(kind: MapKind, date = todayYYYYMMDD()): string {
  return `${date}_${currentInitials()}_${kindLabel(kind)}`;
}

export const refreshMapList = publishMaps;

/**
 * A local change to one map: one transaction in the store, then the screen, then the sync engine
 * is told. `updatedAt` always moves forward — it is how an upload knows whether the version it
 * sent is still the one here.
 */
async function change(id: string, fn: (cur: LocalMap, at: number) => LocalMap | undefined, blobs?: Record<string, Blob>): Promise<LocalMap | undefined> {
  const out = await mutateMap(id, (cur) => {
    if (!cur || cur.deletedAt !== null) return undefined;
    const next = fn(cur, Math.max(now(), cur.updatedAt + 1));
    if (!next) return undefined;
    const { blocked: _blocked, ...rest } = next; // a version the server refused is history now
    return rest;
  }, blobs);
  if (!out) return undefined;
  show(id, out);
  changed(id);
  return out;
}

export async function createMap(kind: MapKind, file: Blob): Promise<LocalMap> {
  const img = await decodeImage(file);
  const thumb = await makeThumbnail(img.bitmap);
  img.bitmap.close();
  const at = now();
  const id = newId();
  const doc = newDoc(kind, img.w, img.h, { date: todayYYYYMMDD(), initials: currentInitials() });
  doc.scale = app.state.settings.markupScale;
  doc.legend = app.state.settings.legendDefault;
  if (kind === 'soils') doc.soils.testStyle = app.state.settings.testStyle;
  // the pinpoints (tests' dots, shots' rings) left off, if Settings says so for new maps
  if (!app.state.settings.dots) doc.noDots = true;
  // elevation tags read as Settings says for new maps: their colour (blue unless chosen otherwise), word and unit (checked as any document is)
  if (kind === 'soils') {
    try {
      const { color, prefix, unit } = app.state.settings.elevDefaults;
      const elev = parseDoc({ ...doc, elev: { color, prefix, unit } }).elev;
      if (elev) doc.elev = elev;
    } catch {
      /* a default that is no longer good is let go */
    }
  }
  const map: LocalMap = {
    id,
    kind,
    title: defaultTitle(kind),
    createdAt: at,
    updatedAt: at,
    deletedAt: null,
    rev: 0,
    dirty: true,
    imageType: img.type,
    imageBytes: img.blob.size,
    imageUploaded: false,
    thumbUploaded: false,
    thumbStamp: at,
    doc,
  };
  // the map and both its pictures, or nothing
  await mutateMap(id, () => map, { [`${id}:orig`]: img.blob, [`${id}:thumb`]: thumb });
  show(id, map);
  changed(id);
  return map;
}

/** Save the document; with `thumb`, the map's new thumbnail too (it goes up after the document). */
export async function saveDoc(id: string, doc: MapDoc, thumb?: Blob): Promise<void> {
  await change(id, (cur, at) => ({ ...cur, doc, updatedAt: at, dirty: true, ...(thumb ? { thumbUploaded: false, thumbStamp: at } : {}) }), thumb ? { [`${id}:thumb`]: thumb } : undefined);
}
/**
 * What the test badges say is the inspector's, on all his maps (2026-10-23, Adam, of Settings' badge
 * style: "DON'T EFFECT, period"): every soils map on this device takes the style chosen — in Settings,
 * or on a map — and goes up with it. (`except`: the map open in the editor, which changes itself.)
 */
export async function restyleTests(style: TestStyle, except?: string): Promise<void> {
  for (const m of app.state.maps) {
    if (m.id === except || m.deletedAt !== null || m.kind !== 'soils' || (m.doc.soils.testStyle ?? 'test') === style) continue;
    await change(m.id, (cur, at) => ((cur.doc.soils.testStyle ?? 'test') === style ? undefined : { ...cur, doc: { ...cur.doc, soils: { ...cur.doc.soils, testStyle: style } }, updatedAt: at, dirty: true }));
  }
}
/** A new thumbnail for a document that is already saved: the document is not sent again for it. */
export async function saveThumb(id: string, thumb: Blob): Promise<void> {
  await change(id, (cur, at) => ({ ...cur, thumbUploaded: false, thumbStamp: at }), { [`${id}:thumb`]: thumb });
}

export async function renameMap(id: string, title: string): Promise<void> {
  await change(id, (cur, at) => (title.trim() && title.trim() !== cur.title ? { ...cur, title: title.trim(), updatedAt: at, dirty: true } : undefined));
}

export async function deleteMap(id: string): Promise<void> {
  // Tombstone locally; sync pushes the deletion, then the record is dropped.
  await change(id, (cur, at) => ({ ...cur, deletedAt: at, updatedAt: at, dirty: true }));
}

export async function mapImage(id: string): Promise<Blob | undefined> {
  return getBlob(`${id}:orig`);
}
export async function mapThumb(id: string): Promise<Blob | undefined> {
  return getBlob(`${id}:thumb`);
}

export function humanDate(map: LocalMap): string {
  const d = new Date(map.createdAt);
  return formatDateHuman(`${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`);
}
